// =============================================================================
// useDragAndDrop - Hook per la logica drag & drop dello scheduler
// =============================================================================

import { useState, useCallback } from 'react';
import { useSensor, useSensors, PointerSensor, DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { parseISO, differenceInMinutes } from 'date-fns';
import i18n from '../../../i18n';
import type { Appointment } from '../../../types';
import { useTenantTimeZone } from '../../../store/tenantStore';
import {
  civilDateInTz,
  civilDayOf,
  minutesOfDayInTz,
  wallClockToUtc,
} from '../../../lib/tenantTime';

interface UseDragAndDropOptions {
  appointments: Appointment[];
  gridStartHour: number;
  gridEndHour: number;
  moveAppointment: { mutate: (data: any, opts?: any) => void; isPending: boolean };
  refetchAppointments: () => void;
  onToast: (toast: { message: string; type: 'error' | 'success' }) => void;
}

export function useDragAndDrop({
  appointments,
  gridStartHour,
  gridEndHour,
  moveAppointment,
  refetchAppointments,
  onToast,
}: UseDragAndDropOptions) {
  const timeZone = useTenantTimeZone();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedAppointment, setDraggedAppointment] = useState<Appointment | null>(null);
  const [draggedRect, setDraggedRect] = useState<{ width: number; height: number } | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const extractAppointmentId = useCallback((draggableId: string): string => {
    const separatorIndex = draggableId.indexOf('__');
    return separatorIndex >= 0 ? draggableId.substring(0, separatorIndex) : draggableId;
  }, []);

  const extractSourceResourceId = useCallback((draggableId: string): string | undefined => {
    const separatorIndex = draggableId.indexOf('__');
    return separatorIndex >= 0 ? draggableId.substring(separatorIndex + 2) : undefined;
  }, []);
  
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const appointmentId = extractAppointmentId(active.id as string);
    setActiveId(appointmentId);
    
    const node = document.querySelector(`[data-appointment-id="${appointmentId}"]`);
    if (node) {
      const rect = node.getBoundingClientRect();
      setDraggedRect({ width: rect.width, height: rect.height });
    } else if (active.rect.current.initial) {
      setDraggedRect({
        width: active.rect.current.initial.width,
        height: active.rect.current.initial.height,
      });
    }
    
    const appointment = appointments.find(apt => apt.id === appointmentId);
    if (appointment) setDraggedAppointment(appointment);
  }, [appointments, extractAppointmentId]);
  
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over, delta } = event;
    setActiveId(null);
    setDraggedAppointment(null);
    setDraggedRect(null);
    
    if (!over) return;
    
    const appointmentId = extractAppointmentId(active.id as string);
    const sourceResourceId = extractSourceResourceId(active.id as string);
    const appointment = appointments.find(apt => apt.id === appointmentId);
    if (!appointment) return;
    
    const overData = over.data.current as { 
      resourceId?: string; hour?: number; dayStart?: Date;
    } | undefined;
    
    let targetResourceId: string | undefined = overData?.resourceId;
    
    if (!targetResourceId) {
      const overId = over.id as string;
      if (overId.startsWith('resource-')) {
        targetResourceId = overId.replace('resource-', '');
      } else if (overId.startsWith('cell-')) {
        const parts = overId.split('-');
        if (parts.length >= 7) {
          targetResourceId = parts.slice(1, 6).join('-');
        }
      }
    }
    
    if (!targetResourceId && !overData?.hour) return;
    
    const originalStart = parseISO(appointment.scheduledStart);
    const originalEnd = parseISO(appointment.scheduledEnd);
    let newStartTime: Date;
    
    if (active.rect.current.initial && active.rect.current.initial.width > 0) {
      let minutesPerPixel: number;
      if (over.rect && over.rect.width > 0 && over.id.toString().startsWith('cell-')) {
        minutesPerPixel = 60 / over.rect.width;
      } else {
        const durationMinutes = differenceInMinutes(originalEnd, originalStart);
        minutesPerPixel = durationMinutes / active.rect.current.initial.width;
      }
      
      const timeDeltaMinutes = delta.x * minutesPerPixel;
      const exactNewStartTime = new Date(originalStart.getTime() + timeDeltaMinutes * 60000);

      // Snap ai quarti d'ora e limiti della griglia sono entrambi espressi
      // nell'orologio dell'ORGANIZZAZIONE: la posizione sulla griglia e' quella.
      // (Alcuni fusi hanno offset di :30 o :45, quindi anche i minuti vanno
      // letti li' e non nel fuso del browser.)
      const dropDay = civilDateInTz(exactNewStartTime, timeZone);
      const exactMinutes = minutesOfDayInTz(exactNewStartTime, timeZone);
      let snappedMinutes = Math.round(exactMinutes / 15) * 15;

      if (snappedMinutes < gridStartHour * 60) {
        snappedMinutes = gridStartHour * 60;
      } else if (snappedMinutes >= gridEndHour * 60) {
        snappedMinutes = (gridEndHour - 1) * 60 + 45;
      }
      newStartTime = wallClockToUtc(dropDay, snappedMinutes, timeZone);
    } else {
      if (overData?.hour !== undefined && overData.dayStart) {
        newStartTime = wallClockToUtc(
          civilDayOf(new Date(overData.dayStart)),
          overData.hour * 60,
          timeZone,
        );
      } else if (targetResourceId) {
        newStartTime = originalStart;
      } else {
        return;
      }
    }
    
    const timeChanged = newStartTime.getTime() !== originalStart.getTime();
    const currentResourceIds = appointment.allocations?.map(a => a.resourceId) || [];
    const resourceChanged = targetResourceId && !currentResourceIds.includes(targetResourceId);
    
    if (!timeChanged && !resourceChanged) return;
    
    const mutationData: { id: string; startTime: string; resourceIds?: string[] } = {
      id: appointmentId,
      startTime: newStartTime.toISOString(),
    };
    
    if (resourceChanged && targetResourceId) {
      const newResourceIds = (appointment.allocations || [])
        .map(alloc => alloc.resourceId === sourceResourceId ? targetResourceId : alloc.resourceId);
      if (sourceResourceId && !newResourceIds.includes(targetResourceId)) {
        newResourceIds.push(targetResourceId);
      }
      mutationData.resourceIds = newResourceIds;
    }
    
    moveAppointment.mutate(mutationData, {
      onError: (error: any) => {
        const message = error.response?.data?.message || i18n.t('dragDrop.moveError', { ns: 'scheduler' });
        onToast({ message, type: 'error' });
        refetchAppointments();
      },
      onSuccess: () => {
        onToast({ message: i18n.t('dragDrop.moveSuccess', { ns: 'scheduler' }), type: 'success' });
      },
    });
  }, [appointments, moveAppointment, refetchAppointments, extractAppointmentId, extractSourceResourceId, gridStartHour, gridEndHour, onToast, timeZone]);

  return {
    activeId,
    draggedAppointment,
    draggedRect,
    sensors,
    handleDragStart,
    handleDragEnd,
  };
}
